import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface VideoControlsProps {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  /** Current time relative to segment start (0 = start of cropped segment) */
  currentTime: number;
  /** Total duration of the cropped segment in seconds */
  duration: number;
  /** Segment start time in video (for seeking) */
  segmentStart: number;
  /** Callback when time changes via seeking */
  onSeek?: (time: number) => void;
}

// LocalStorage keys for volume persistence
const VOLUME_STORAGE_KEY = "wixar-video-volume";
const MUTED_STORAGE_KEY = "wixar-video-muted";

/**
 * Format time in seconds to MM:SS format
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Video playback controls component
 * Shows play/pause button, seek bar, and time display
 */
const VideoControls: React.FC<VideoControlsProps> = ({
  videoRef,
  currentTime,
  duration,
  segmentStart,
  onSeek,
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Volume state - load from localStorage or default to 0.5
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
    return saved !== null ? parseFloat(saved) : 0.5;
  });
  // Muted state - load from localStorage, default to true for first-time autoplay
  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem(MUTED_STORAGE_KEY);
    return saved !== null ? saved === "true" : true;
  });
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const volumeControlRef = useRef<HTMLDivElement>(null);

  // Track the video element we've attached listeners to
  const attachedVideoRef = useRef<HTMLVideoElement | null>(null);

  // Stable event handler refs to avoid stale closures
  const handlePlayEvent = useCallback(() => setIsPlaying(true), []);
  const handlePauseEvent = useCallback(() => setIsPlaying(false), []);

  // Sync playing state with video and attach listeners
  // This effect runs when currentTime changes (which happens when video plays)
  // and attaches listeners when video becomes available
  useEffect(() => {
    const video = videoRef.current;

    // If no video yet, nothing to do
    if (!video) {
      return undefined;
    }

    // If already attached to this video, just sync the playing state
    if (video === attachedVideoRef.current) {
      setIsPlaying(!video.paused);
      return undefined;
    }

    // Clean up old listeners if attached to a different video
    if (attachedVideoRef.current) {
      attachedVideoRef.current.removeEventListener("play", handlePlayEvent);
      attachedVideoRef.current.removeEventListener("pause", handlePauseEvent);
    }

    // Attach listeners to new video
    attachedVideoRef.current = video;
    video.addEventListener("play", handlePlayEvent);
    video.addEventListener("pause", handlePauseEvent);

    // Apply audio settings to new video element
    // (new videos are created muted for autoplay, we need to apply user's settings)
    video.volume = volume;
    video.muted = isMuted;

    // Set initial state
    setIsPlaying(!video.paused);

    return () => {
      if (attachedVideoRef.current) {
        attachedVideoRef.current.removeEventListener("play", handlePlayEvent);
        attachedVideoRef.current.removeEventListener("pause", handlePauseEvent);
        attachedVideoRef.current = null;
      }
    };
  }, [currentTime, handlePlayEvent, handlePauseEvent, videoRef]);

  // Sync volume with video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = volume;
    video.muted = isMuted;
  }, [videoRef, volume, isMuted]);

  // Save volume and muted state to localStorage when they change
  useEffect(() => {
    localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString());
  }, [volume]);

  useEffect(() => {
    localStorage.setItem(MUTED_STORAGE_KEY, isMuted.toString());
  }, [isMuted]);

  // Close volume slider when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(e.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    if (showVolumeSlider) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [showVolumeSlider]);

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch((e) => console.warn("Play failed:", e));
    } else {
      video.pause();
    }
  }, [videoRef]);

  const handleToggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    // Unmute when user adjusts volume
    if (newVolume > 0) {
      setIsMuted(false);
    }
  }, []);

  // Calculate time from mouse/touch position
  const getTimeFromPosition = useCallback(
    (clientX: number): number => {
      const progressBar = progressBarRef.current;
      if (!progressBar || duration <= 0) return 0;

      const rect = progressBar.getBoundingClientRect();
      const position = (clientX - rect.left) / rect.width;
      const clampedPosition = Math.max(0, Math.min(1, position));
      return clampedPosition * duration;
    },
    [duration],
  );

  // Seek video to specific time
  const seekTo = useCallback(
    (relativeTime: number) => {
      const video = videoRef.current;
      if (!video) return;

      const absoluteTime = segmentStart + relativeTime;
      video.currentTime = absoluteTime;
      onSeek?.(relativeTime);
    },
    [videoRef, segmentStart, onSeek],
  );

  // Handle click on progress bar (instant seek)
  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const time = getTimeFromPosition(e.clientX);
      seekTo(time);
    },
    [getTimeFromPosition, seekTo],
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const time = getTimeFromPosition(clientX);
      setDragValue(time);
    },
    [getTimeFromPosition],
  );

  // Handle drag move and end (document-level for smooth dragging)
  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const time = getTimeFromPosition(clientX);
      setDragValue(time);
    };

    const handleEnd = (e: MouseEvent | TouchEvent) => {
      const clientX = "changedTouches" in e ? e.changedTouches[0].clientX : e.clientX;
      const time = getTimeFromPosition(clientX);
      seekTo(time);
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, getTimeFromPosition, seekTo]);

  // Display value: drag value when dragging, otherwise current time
  const displayTime = isDragging ? dragValue : currentTime;
  const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
      {/* Play/Pause button */}
      <button
        onClick={handlePlayPause}
        className="text-white hover:text-green-400 transition-colors focus:outline-none"
        title={isPlaying ? t("statsNew.gazeHeatmap.pause") : t("statsNew.gazeHeatmap.play")}
      >
        {isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Volume control */}
      <div
        ref={volumeControlRef}
        className="relative flex items-center gap-2 group/volume"
        onMouseEnter={() => setShowVolumeSlider(true)}
        onMouseLeave={() => setShowVolumeSlider(false)}
      >
        <button
          onClick={handleToggleMute}
          className="text-white hover:text-green-400 transition-colors focus:outline-none"
          title={isMuted ? t("statsNew.gazeHeatmap.unmute") : t("statsNew.gazeHeatmap.mute")}
        >
          {isMuted || volume === 0 ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
              />
            </svg>
          ) : volume < 0.5 ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.536 8.464a5 5 0 010 7.072M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
          )}
        </button>

        {/* Horizontal volume slider - appears on hover */}
        <div
          className={`flex items-center overflow-hidden transition-all duration-200 ${
            showVolumeSlider ? "w-20 opacity-100" : "w-0 opacity-0"
          }`}
        >
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={handleVolumeChange}
            className="w-full h-1 bg-white/30 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:shadow-md"
          />
        </div>
      </div>

      {/* Current time */}
      <span className="text-white text-sm font-mono min-w-[40px]">{formatTime(displayTime)}</span>

      {/* Progress bar */}
      <div
        ref={progressBarRef}
        className="relative flex-1 h-2 min-w-[100px] cursor-pointer group"
        onClick={handleProgressClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        {/* Background track */}
        <div className="absolute inset-0 bg-white/30 rounded-full" />

        {/* Progress fill */}
        <div
          className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-none"
          style={{ width: `${progressPercent}%` }}
        />

        {/* Thumb indicator */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progressPercent}% - 6px)` }}
        />
      </div>

      {/* Duration */}
      <span className="text-white text-sm font-mono min-w-[40px]">{formatTime(duration)}</span>
    </div>
  );
};

export default VideoControls;
